import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import logo from "../../assets/WALLS_logo-white.png";
import LazyOnScroll from "../../ui/LazyOnScroll";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isNotHome = location.pathname !== "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium underline-offset-4 hover:underline hover:text-[#484331] ${
      isActive ? "text-[#484331] underline" : ""
    }`;

  const mobileLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `text-base font-medium px-3 py-2 rounded-md transition-colors ${
      isActive
        ? "bg-[#ecebe2] text-[#484331]"
        : "text-[#484331] hover:bg-[#f1f0e7]"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-shadow ${
        isScrolled || isNotHome ? "shadow-md" : ""
      }`}
    >
      {/* Main Navigation Bar */}
      <LazyOnScroll>
        <div className="container max-w-screen-2xl flex h-16 items-center justify-between mx-auto px-4 md:px-6 lg:px-12 xl:px-20">
          <NavLink to="/" className="flex items-center gap-2">
            <img
              src={logo}
              alt="logo"
              className="h-16 w-auto object-contain"
              style={{ maxHeight: "64px" }}
            />
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-6">
            <NavLink to="/" className={navLinkClasses}>
              Home
            </NavLink>
            <NavLink to="/about" className={navLinkClasses}>
              About
            </NavLink>
            <NavLink to="/portfolio" className={navLinkClasses}>
              Portfolio
            </NavLink>
            <NavLink to="/contact" className={navLinkClasses}>
              Contact
            </NavLink>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex">
            <NavLink to="/contact">
              <Button className="bg-[#484331] hover:bg-[#3e3a2b] text-white">
                Get in Touch
              </Button>
            </NavLink>
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </LazyOnScroll>

      {/* Mobile Navigation with Transition */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out md:hidden ${
          isMenuOpen
            ? "max-h-[600px] opacity-100 scale-100"
            : "max-h-0 opacity-0 scale-95"
        }`}
      >
        <div className="container max-w-screen-2xl mx-auto px-4 md:px-6">
          <nav className="flex flex-col gap-1 p-4">
            <NavLink
              to="/"
              className={mobileLinkClasses}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </NavLink>
            <NavLink
              to="/about"
              className={mobileLinkClasses}
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </NavLink>
            <NavLink
              to="/portfolio"
              className={mobileLinkClasses}
              onClick={() => setIsMenuOpen(false)}
            >
              Portfolio
            </NavLink>
            <NavLink
              to="/contact"
              className={mobileLinkClasses}
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </NavLink>
            <NavLink to="/contact" onClick={() => setIsMenuOpen(false)}>
              <Button className="w-full bg-[#484331] hover:bg-[#3e3a2b] text-white">
                Get in Touch
              </Button>
            </NavLink>
          </nav>
        </div>
      </div>
    </header>
  );
}
